import type { ReactNode } from 'react';
import { useLingui } from '@lingui/react';
import { Lock } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * The lock on a change request that "Approve all" leaves out, with the reason
 * in a tooltip. Focusable, so keyboard and screen reader users reach the
 * reason too; a click stays on the lock and does not open the row.
 */
export function BatchLockHint({ reason, className }: { reason: ReactNode; className?: string }) {
  const { i18n } = useLingui();
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            tabIndex={0}
            aria-label={i18n._({ id: 'workflow.triage.lock.label', message: 'Not included in Approve all' })}
            className={cn(
              'inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              className,
            )}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            data-testid="batch-lock"
          >
            <Lock className="size-3.5" aria-hidden="true" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
